import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { Crosshair, Search } from "lucide-react";

export default function WeakSpots() {
  const [q, setQ] = useState("");
  const [showSug, setShowSug] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["weakSpots"], queryFn: () => api.get("/weak-spots").then((r) => r.data), refetchInterval: 600000 });
  const { data: hrData } = useQuery({ queryKey: ["hrAlerts"], queryFn: () => api.get("/hr-alerts").then((r) => r.data) });
  const teamHRs = {};
  (hrData?.alerts || []).forEach((a) => { if (a.team) teamHRs[a.team] = (teamHRs[a.team] || 0) + 1; });
  const all = data?.matchups || [];
  const matchups = q.trim()
    ? all.filter((m) => `${m.name} ${m.team} ${m.opponent} ${m.game}`.toLowerCase().includes(q.trim().toLowerCase()))
    : all;
  const suggestions = q.trim().length >= 1 && showSug
    ? [...new Set(all.flatMap((m) => [m.name, m.opponent, m.team]))]
        .filter((s) => s && s.toLowerCase().includes(q.trim().toLowerCase()) && s.toLowerCase() !== q.trim().toLowerCase())
        .slice(0, 6)
    : [];

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Weak <span className="gold-text">Spots</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Where tonight's starters get hurt. Lineups show the team's <b className="text-neutral-300">most recent real lineup</b> until today's is officially announced, then flip to confirmed automatically. Gold rows are the smash spots.</p>
      </div>
      <div className="relative z-50 max-w-md fade-up-1">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a855f7]" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setShowSug(true); }} onBlur={() => setTimeout(() => setShowSug(false), 150)}
          placeholder="Search a pitcher or team... e.g. Skenes, Yankees" data-testid="weakspots-search-input"
          className="w-full bg-[#121212] border border-white/15 focus:border-[#a855f7] rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none transition-colors duration-200" />
        {suggestions.length > 0 && (
          <div className="absolute z-40 mt-1 w-full bg-[#0d0d0d] border border-[#a855f7]/50 rounded-lg shadow-2xl shadow-black overflow-hidden" data-testid="weakspots-suggestions">
            {suggestions.map((s) => (
              <button key={s} onMouseDown={() => { setQ(s); setShowSug(false); }} data-testid={`weakspots-suggestion-${s.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                className="flex items-center gap-2 w-full text-left px-3 py-2.5 text-sm font-semibold text-white hover:bg-[#a855f7]/15 hover:text-[#a855f7] border-b border-white/5 last:border-0 transition-colors duration-200">
                <Search size={12} className="text-[#a855f7] shrink-0" /> {s}
              </button>
            ))}
          </div>
        )}
      </div>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Mapping the weak spots...</div>}
      {!isLoading && q.trim() && matchups.length === 0 && (
        <p className="text-neutral-500 text-sm" data-testid="weakspots-search-empty">No matchup found for "{q}" on tonight's slate.</p>
      )}
      <div className="grid lg:grid-cols-2 gap-4 fade-up-1">
        {matchups.map((m) => {
          const hrs = teamHRs[m.opponent] || 0;
          const hot = hrs >= 2;
          return (
          <div key={`${m.id}-${m.game}`} className={`card-surface rounded-xl p-4 ${hot ? "border-[#a855f7] gold-glow glow-pulse" : ""}`} data-testid={`weakspot-card-${m.id}`}>
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <div>
                <div className="font-display text-lg font-bold">{m.name} <span className="text-neutral-500 text-xs font-normal">({m.team})</span></div>
                <div className="text-[11px] text-neutral-500 flex items-center gap-2 flex-wrap">
                  vs {m.opponent} · ERA {m.era ?? "—"} · HR/9 {m.hr9 ?? "—"}
                  <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${m.lineup_status === "confirmed" ? "border-[#22c55e]/50 text-[#22c55e] bg-[#22c55e]/10" : "border-white/20 text-neutral-400"}`} data-testid={`lineup-status-${m.id}`}>
                    {m.lineup_status === "confirmed" ? "Confirmed lineup" : "Projected lineup"}
                  </span>
                  {hot && (
                    <span className="text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded-full bg-[#a855f7] text-black animate-pulse" data-testid={`weakspot-hot-${m.id}`}>
                      {m.opponent.split(" ").pop()} {hrs} HRs today
                    </span>
                  )}
                </div>
              </div>
              <div className={`font-data text-xl font-bold ${m.vulnerability >= 7 ? "text-red-400" : m.vulnerability >= 4.5 ? "text-[#f59e0b]" : "text-[#22c55e]"}`}>
                {m.vulnerability}<span className="text-neutral-600 text-xs">/10</span>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {m.slots.map((s) => (
                <Link to={`/research/${s.mlb_id}`} key={s.order}
                  className={`flex items-center gap-3 px-2 py-1.5 rounded text-sm transition-colors duration-200 ${s.weak_spot ? "bg-[#a855f7]/10 border border-[#a855f7]/40" : "hover:bg-white/5 border border-transparent"}`}>
                  <span className="font-data text-xs text-neutral-500 w-4">{s.order}</span>
                  <span className={`flex-1 truncate ${s.weak_spot ? "text-[#a855f7] font-semibold" : "text-neutral-300"}`}>
                    {s.batter} {s.weak_spot && <Crosshair size={12} className="inline ml-1" />}
                  </span>
                  <span className="font-data text-xs text-neutral-500">{s.ops ?? "—"} OPS</span>
                  <span className={`font-data text-xs font-bold w-10 text-right ${s.weak_spot ? "text-[#22c55e]" : "text-neutral-400"}`}>{s.threat ?? "—"}</span>
                </Link>
              ))}
              {m.slots.length === 0 && <p className="text-xs text-neutral-600 p-2">Opponent lineup data not available.</p>}
            </div>
          </div>
          );
        })}
      </div>
      {data?.locked && <UpgradeGate feature={`All weak spot breakdowns (${data.total} matchups tonight)`} />}
    </div>
  );
}
